import React, { useState } from "react";
import { Drawer, Upload, Modal, notification } from "antd";
import { UploadOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import styles from "./PayInstallment.module.css";
import { arrayUnion, doc, getDoc, updateDoc } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid'; 
import { findUserWhoReferedMe } from "../../../../../service/findUsers/findUserWhoReferedMe";
import { updateUserEarnings } from "../../../../../service/updateEarnings/updateUserEarnings";
import ButtonComponent from "../../../../../components/CssComponents/ButtonComponent/ButtonComponent";
import { saveTransaction } from "../../../../../service/saveTransactions";
import { db } from "../../../../../firebase-config";
import qrCode from "../../../../../assets/qr_code.jpg"


const PayInstallment = ({
    isVisible,
    onClose,
    buyItemType,
    buyItemPrice,
    buyItemCrossPrice,
    buyItemName,
    buyItemId,
    buyItemBanner,
    uid
}) => {
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [fileList, setFileList] = useState([]);
    const [pendingUploads, setPendingUploads] = useState([]);

    const handlePreview = async (file) => {
        setPreviewImage(file.url);
        setPreviewVisible(true);
    };

    const handleCancelPreview = () => setPreviewVisible(false);

    // Handle file removal
    const handleRemove = (file) => {
        setPendingUploads(prev => prev.filter(f => f.uid !== file.uid));
        setFileList(prev => prev.filter(f => f.uid !== file.uid));
    };

    // Handle file selection
    const handleChange = ({ fileList: newFileList }) => {
        // Validate and prevent duplicate filenames
        const uniqueFiles = newFileList.filter((file, index, self) =>
            index === self.findIndex((f) => f.name === file.name)
        );

        if (uniqueFiles.length !== newFileList.length) {
            notification.warning({
                message: "Duplicate File",
                description: "A file with the same name already exists.",
                placement: "topRight",
                duration: 3,
            });
        }

        setPendingUploads(uniqueFiles);
        setFileList(uniqueFiles.map(file => ({
            ...file,
            status: 'done',
            url: file.url || URL.createObjectURL(file.originFileObj)
        })));
    };

    

    // Upload all pending files to Firebase
    const uploadFilesToFirebase = async () => {
        try {
            const storage = getStorage();
            const uploadPromises = pendingUploads.map(async (file) => {
                // Generate unique filename using UUID
                const uniqueFileName = `${uuidv4()}-${file.name}`;
                const storageRef = ref(storage, `lms_payments/${uniqueFileName}`);
                const snapshot = await uploadBytes(storageRef, file.originFileObj);
                return getDownloadURL(snapshot.ref);
            });

            const urls = await Promise.all(uploadPromises);
            return urls;
        } catch (error) {
            console.error("Error uploading files:", error);
            throw error;
        }
    };

    const handleBuyClick = async () => {
        try {

            if (!uid) {
                notification.warning({
                    message: "Kindly Login",
                    description: "Kindly Login.",
                    placement: "topRight",
                    duration: 3,
                });
                return;
            }
            // Upload all pending files
            const uploadedUrls = await uploadFilesToFirebase();

            // Validate Proof URLs
            if (!uploadedUrls.length) {
                notification.warning({
                    message: "Upload Required",
                    description: "Please upload at least one proof of payment before proceeding.",
                    placement: "topRight",
                    duration: 3,
                });
                return;
            }

            // Fetch the User Document
            const userRef = doc(db, "users", uid);
            const userSnapshot = await getDoc(userRef);

            if (!userSnapshot.exists()) {
                notification.warning({
                    message: "User Data Not Found",
                    description: "Contact Admin to resolve this issue.",
                    placement: "topRight",
                    duration: 3,
                });
                return;
            }

            const userData = userSnapshot.data();

            if (userData?.userTypes !== "team_leader") {
                notification.warning({
                    message: "Access Denied",
                    description: "Only students can purchase items.",
                    placement: "topRight",
                    duration: 3,
                })
                return;
            }

            // Check if User Already Has the items
            if (userData?.buyItemIdsArray?.some((entry) => entry.buyItemId === buyItemId)) {
                notification.warning({
                    message: "Already Enrolled",
                    description: "You have already enrolled.",
                    placement: "topRight",
                    duration: 3,
                });
                return;
            }

            const referringUserUid = await findUserWhoReferedMe(userData.referralId);

            console.log("Referring User UID:", referringUserUid);

            if (!referringUserUid) {
                notification.error({
                    message: "No Referrer Found",
                    description: "Contact Admin to resolve this issue.",
                    placement: "topRight",
                    duration: 3,
                });
                console.error("No referrer found for user:", uid);
                return;
            }

            const newItemEntry = {
                buyItemId: buyItemId || "ar_id",
                buyItemType: buyItemType || "ar_type",
                buyItemPrice: buyItemPrice || 0,
                proofUrls: uploadedUrls,
                status: "pending",
            };

            await updateDoc(userRef, {
                buyItemIdsArray: arrayUnion(newItemEntry),
            });

            if (referringUserUid) {
                await updateUserEarnings(
                    {
                        referringUserUid: referringUserUid,
                        buyItemPrice: buyItemPrice,
                        buyItemId: buyItemId,
                        studentUid: uid,
                    }
                )
            }

            await saveTransaction({
                type:"student_payment",
                direction:"debit",
                uid: uid,
                amount: buyItemPrice,
                itemId: buyItemId,
                reason: `Purchased ${buyItemName} (${buyItemType})`,
                proofUrls: uploadedUrls,
            });

            notification.success({
                message: "Congratulations!",
                description: "You have purchased the item successfully.",
                placement: "topRight",
                duration: 3,
            });

            onClose();
        } catch (error) {
            console.error("Error purchasing item:", error);
            notification.error({
                message: "Purchase Failed",
                description: "There was an error processing your item purchase.",
                placement: "topRight",
                duration: 3,
            });
        }
    };



    return (
        <Drawer
            title={"Item Details"}
            placement="right"
            onClose={onClose}
            open={isVisible}
            width={350}
            closeIcon={<CloseCircleOutlined style={{ color: "#b17e32" }} />}
        >
            <div className={styles.container}>
                {/* Header Section */}
                <div className={styles.header}>
                    <img
                        src={buyItemBanner || "https://via.placeholder.com/150"}
                        alt={buyItemName}
                        className={styles.courseImage}
                    />
                    <div className={styles.courseInfo}>
                        <div className={styles.courseTitle}>{buyItemName}</div>
                        <div className={styles.coursePrice}>
                            Buy Price : {buyItemPrice}
                        </div>
                        <div className={styles.coursePrice}>
                            Original Price : {buyItemCrossPrice}
                        </div>
                    </div>
                </div>

                {/* QR Code Section */}
                <div className={styles.qrCodeSection}>
                    <img src={qrCode} alt="QR Code" className={styles.qrCodeImage} />
                    <p className={styles.qrCodeText}>Scan QR Code to Pay!</p>
                </div>

                {/* Upload Section */}
                <div className={styles.uploadSection}>
                    <Upload
                        fileList={fileList}
                        onChange={handleChange}
                        onPreview={handlePreview}
                        onRemove={handleRemove}
                        listType="picture-card"
                        accept="image/*"
                        beforeUpload={() => false} // Prevent auto-upload
                    >
                        {fileList.length < 5 && (
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Upload Screenshot</div>
                            </div>
                        )}
                    </Upload>

                    <Modal
                        open={previewVisible}
                        footer={null}
                        onCancel={handleCancelPreview}
                    >
                        <img alt="Preview" style={{ width: "100%" }} src={previewImage} />
                    </Modal>
                </div>

                <div className={styles.actions}>
                    <ButtonComponent className={styles.btn} onClick={handleBuyClick} buttonText={"Buy"} />
                    <ButtonComponent className={styles.btn} buttonText={"Cancel"} onClick={onClose} />
                </div>
            </div>
        </Drawer>
    );
};

export default PayInstallment;